/**
 * useSchedulingRealtime - Hook de Realtime para sincronização de agendamentos
 * Escuta mudanças em crm_deal_activities e scheduling_bookings via Supabase Realtime
 * e invalida caches relevantes automaticamente.
 *
 * @version 1.0.0
 * RELEASE: 2026-03-30 v1.0.0 - Implementação inicial
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger dedicado para este hook
const logger = createLogger('SchedulingRealtime');

/**
 * Chaves de cache que devem ser invalidadas ao receber mudanças de realtime
 */
const SCHEDULING_QUERY_KEYS = [
  'calendar-activities',
  'crm-activities-feed',
  'scheduling-list-activities',
  'standalone-bookings',
  'scheduling-kpi',
  'customer-activities',
];

/**
 * Hook que assina mudanças Realtime nas tabelas de agendamento
 * e invalida os caches relevantes para manter a UI sincronizada.
 *
 * Deve ser chamado uma vez na página de agendamentos (CrmActivitiesCalendar).
 */
export function useSchedulingRealtime() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  useEffect(() => {
    if (!tenantId) return;

    logger.debug('Iniciando assinatura Realtime para agendamentos', { tenantId });

    // Canal único para ambas as tabelas, filtrado por tenant_id
    const channel = supabase
      .channel(`scheduling-rt-${tenantId}`)
      // Escuta mudanças em crm_deal_activities (cancelar, reabrir, concluir)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_deal_activities',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          logger.debug('Realtime: crm_deal_activities alterado', { eventType: payload.eventType });
          // Invalida todos os caches de agendamento
          for (const key of SCHEDULING_QUERY_KEYS) {
            queryClient.invalidateQueries({ queryKey: [key] });
          }
        }
      )
      // Escuta mudanças em scheduling_bookings (cancelar, confirmar)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'scheduling_bookings',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          logger.debug('Realtime: scheduling_bookings alterado', { eventType: payload.eventType });
          for (const key of SCHEDULING_QUERY_KEYS) {
            queryClient.invalidateQueries({ queryKey: [key] });
          }
        }
      )
      .subscribe((status) => {
        logger.debug('Realtime subscription status:', status);
      });

    // Cleanup ao desmontar ou mudar tenant
    return () => {
      logger.debug('Removendo assinatura Realtime de agendamentos');
      supabase.removeChannel(channel);
    };
  }, [tenantId, queryClient]);
}
